"use strict"

import React, { useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"

import { getObjectsList } from "../../../../Loaders"
import { logToServer } from "../../../../ErrorBoundary"
import PageLoadingPanel from "../../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../../shared_components/widgets/PageErrorPanel"
import { stringifyDate } from "../../../../shared_components/utils"
import { getFormattedMessage } from "../../../shared_components_registration"
import { withAppSettingsConfig } from "../../../../config/AppSettingsConfigProvider"
import { withFermManagementConfig } from "../../../../config/FermManagementConfigProvider"
import {
  getLocaleFormattedUserString,
  LocaleFormattedUser,
} from "../../../internationalization/LocaleFormatted"
import EntityInformation from "./EntityInformation"
import NewProjectForm from "./NewProjectForm"
import NewEntityForm from "./NewEntityForm"
import { ActiveSpan, statusOptions, StatusDropdown, UserDropdown } from "./utils"
import ReactSelectionElement from "../../../../shared_components/widgets/ReactSelectionElement"
import RunTable from "../../RunManagement/RunTable"
import { useSensePageWidth } from "../../../samplemanagement/SampleScheduleTab/FormSamplePoint"

const SubfieldHeader = ({
  onToggle,
  parentID,
  type = "task",
  expManagerField = {},
  entity = {},
  creating = false,
  getTextStr,
  disabled = true,
  loadProjects,
  toggleSectionExpand,
  permissions,
  nonFilterArr = [],
  statuses = [],
  taskFields,
  isActive,
  setIsActive,
}) => {
  const isTask = type === "task"
  const permission = permissions.includes(isTask ? "edit_project_task" : "edit_experiment")
  const singular = isTask
    ? getTextStr("general.project.task", "Task")
    : getTextStr("general.project.experiment", "Experiment")
  const plural = isTask
    ? getTextStr("general.project.tasks", "Tasks")
    : getTextStr("general.project.experiments", "Experiments")
  return (
    <>
      <div style={{ display: "flex" }}>
        <h5>
          <b>{`${entity.name}: ${plural}`}</b>
        </h5>
        <Button
          variant={"outline-primary"}
          size={"sm"}
          style={{ marginLeft: "auto" }}
          disabled={disabled || !entity.active || !permission}
          onClick={onToggle}
        >
          <span className={creating ? "fa fa-caret-down" : "fa fa-caret-right"} />
          &nbsp;
          <span className={"fa fa-plus-circle"} />
          &nbsp;
          {`${getTextStr("general.add", "Add")} '${entity.name}' ${singular}`}
        </Button>
      </div>
      {creating && (
        <NewEntityForm
          type={type}
          parentID={parentID}
          getTextStr={getTextStr}
          onSuccess={(entityID) => {
            loadProjects()
            toggleSectionExpand(entityID, type)
          }}
          permission={permission}
          expManagerField={expManagerField}
        />
      )}
      <hr style={{ margin: "10px 0px" }} />
      {nonFilterArr.length > 0 && (
        <>
          <div className={"row"}>
            <div className={"col-md-3"}>
              <StatusDropdown
                showLabel={true}
                options={statuses}
                defaultValue={statuses.find((s) => s.value === isActive)}
                setValue={(val) => setIsActive(val)}
                disabled={nonFilterArr.length === 0}
                prefix={`[${singular}]`}
              />
            </div>
          </div>
          <div className={"row"} style={{ margin: "0" }}>
            {taskFields(type).map((r, i) => (
              <div
                className={`col-${r.col} text-center solid-grey-border`}
                style={{ borderBottom: "none", padding: "5px" }}
                key={`${parentID}${type}Header${i}`}
              >
                {r.id && getTextStr(r.id, r.msg)}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

const EntityCountSpan = ({ entities = [], activeKey = "active" }) => (
  <span>
    {entities.length}&nbsp;
    {entities.length > 0 && (
      <>
        (<span className={"text-success"}>{entities.filter((t) => t[activeKey]).length}</span>&nbsp;/&nbsp;
        <span className={"text-danger"}>{entities.filter((t) => !t[activeKey]).length}</span>)
      </>
    )}
  </span>
)

function ProjectHierarchyModal({
  show,
  onHide,
  title = "",
  intl,
  // From AppSettingsConfigProvider
  activeUsers = [],
  config = {},
  // From FermManagementConfigProvider
  getExperiments,
  IS_PROJECT_EXPERIMENT_SITE = false,
}) {
  const pageWidth = useSensePageWidth()
  const { permissions = [] } = config.user || {}
  const getTextStr = (id, msg, values) => getFormattedMessage(id, msg, true, intl, values)
  const [projects, setProjects] = useState([])
  const [finalProjects, setFinalProjects] = useState([])
  const [netsuiteProjects, setNetsuiteProjects] = useState([])
  const [projectManagers, setProjectManagers] = useState([])
  const [experimentManagers, setExperimentManagers] = useState([])
  const [runOptions, setRunOptions] = useState([])
  const [projectSectionExpanded, setProjectSectionExpanded] = useState({})
  const [taskSectionExpanded, setTaskSectionExpanded] = useState({})
  const [expSectionExpanded, setExpSectionExpanded] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const initializeExpansionState = (projArr) => {
    const projectExpandState = {}
    const taskExpandState = {}
    const experimentExpandState = {}
    projArr.forEach((proj) => {
      const { id, tasks = [] } = proj
      const projectID = String(id)
      projectExpandState[projectID] = false
      tasks.forEach((t) => {
        const { experiments = [] } = t
        taskExpandState[String(t.id)] = false
        experiments.forEach((e) => (experimentExpandState[String(e.id)] = false))
      })
    })
    setProjectSectionExpanded(projectExpandState)
    setTaskSectionExpanded(taskExpandState)
    setExpSectionExpanded(experimentExpandState)
  }

  const [taskTextFilter, setTaskTextFilter] = useState("")
  const [expTextFilter, setExpTextFilter] = useState("")
  const [projectIsActive, setProjectIsActive] = useState(true)
  const [projectManagerFilter, setProjectManagerFilter] = useState({})
  const [experimentManagerFilter, setExperimentManagerFilter] = useState({})
  const [netsuiteProjectFilter, setNetsuiteProjectFilter] = useState(null)
  const [runFilter, setRunFilter] = useState(null)
  const runNames = (runFilter || []).map((r) => r.name)
  const [taskIsActive, setTaskIsActive] = useState(true)
  const [expIsActive, setExpIsActive] = useState(true)
  const doActiveFilterArray = (arr, type = "project") => {
    const isActive = type === "project" ? projectIsActive : type === "task" ? taskIsActive : expIsActive
    return arr.filter(
      (e) => (isActive === true && e.active) || (isActive === false && !e.active) || isActive === null,
    )
  }
  const clearFilters = () => {
    setTaskTextFilter("")
    setExpTextFilter("")
    setProjectIsActive(true)
    setTaskIsActive(true)
    setExpIsActive(true)
    setProjectManagerFilter({})
    setExperimentManagerFilter({})
    setNetsuiteProjectFilter(null)
    setRunFilter(null)
  }
  const getTasksRuns = (tasks) =>
    tasks
      .flat()
      .map((t) => t.experiments)
      .flat()
      .map((e) => e.runs)
      .flat()
  const getDropdownUsers = (arr) =>
    arr.map((u) => ({ value: u, label: getLocaleFormattedUserString(u, intl) }))
  const loadProjects = (initialLoad = false) => {
    setLoading(true)
    setError("")
    if (!initialLoad) getExperiments() // Make sure experiments loaded for run start modal with any changes
    getObjectsList("/warehouse/projects/")
      .then((projArr) => {
        setLoading(false)
        setNetsuiteProjects(
          [...new Set(projArr.map((p) => p.netsuite_project_id))]
            .map((p) => ({ value: p }))
            .sort((a, b) => a.value.localeCompare(b.value)),
        )
        setProjectManagers(
          getDropdownUsers([
            ...new Map(projArr.map((p) => p.project_manager).map((u) => [u.id, u])).values(),
          ]),
        )
        setExperimentManagers(
          getDropdownUsers([
            ...new Map(
              projArr
                .map((p) => p.tasks)
                .flat()
                .map((t) => t.experiments)
                .flat()
                .map((exp) => [exp.experiment_manager.id, exp.experiment_manager]),
            ).values(),
          ]),
        )
        setRunOptions(
          [
            ...new Map(
              getTasksRuns(projArr.map((p) => p.tasks).flat()).map((r) => [
                r.id,
                { name: r.name, reactor: r.reactor, value: `${r.name} [${r.reactor}]` },
              ]),
            ).values(),
          ].sort((a, b) => a.name.localeCompare(b.name)),
        )
        setProjects(projArr)
        initialLoad && initializeExpansionState(projArr)
      })
      .catch((err) => {
        logToServer(err)
        console.error(err)
        setError(getFormattedMessage("general.server.error", "Error! Could not process request"))
        setLoading(false)
      })
  }
  useEffect(() => {
    let projArr = doActiveFilterArray(projects)
    if (projectManagerFilter?.id) {
      projArr = projArr.filter((p) => p.project_manager_id === projectManagerFilter?.id)
    }
    if (experimentManagerFilter?.id) {
      projArr = projArr.filter((p) =>
        p.tasks
          .map((t) => t.experiments)
          .flat()
          .map((e) => e.experiment_manager_id)
          .includes(experimentManagerFilter.id),
      )
    }
    if (netsuiteProjectFilter && netsuiteProjectFilter.length > 0) {
      const netsuiteProjects = netsuiteProjectFilter.map((n) => n.value)
      projArr = projArr.filter((p) => netsuiteProjects.includes(p.netsuite_project_id))
    }
    if (runFilter && runFilter.length > 0) {
      projArr = projArr.filter((p) => {
        const projectRunNames = new Set(getTasksRuns(p.tasks).map((r) => r.name))
        return runNames.some((n) => projectRunNames.has(n))
      })
    }
    if (taskTextFilter) {
      projArr = projArr.filter((p) =>
        p.tasks
          .map((t) => `${t.name} ${t.description}`)
          .join(", ")
          .toLowerCase()
          .includes(taskTextFilter.toLowerCase()),
      )
    }
    if (expTextFilter) {
      projArr = projArr.filter((p) =>
        p.tasks
          .map((t) => t.experiments)
          .flat()
          .map((e) => `${e.name} ${e.description}`)
          .join(", ")
          .toLowerCase()
          .includes(expTextFilter.toLowerCase()),
      )
    }
    if (projArr.length === 1 && !projectSectionExpanded[projArr[0]?.id]) {
      toggleSectionExpand(projArr[0]?.id, "project")
    }
    setFinalProjects(projArr)
  }, [
    projects,
    projectIsActive,
    projectManagerFilter,
    experimentManagerFilter,
    netsuiteProjectFilter,
    runFilter,
    taskTextFilter,
    expTextFilter,
  ])

  useEffect(() => {
    if (!show) return
    loadProjects(true)
  }, [show])

  const rowStyle = (color) => ({
    padding: "10px",
    margin: "0",
    cursor: "pointer",
    borderRadius: "5px",
    backgroundColor: color,
  })
  const [creatingTask, setCreatingTask] = useState(false)
  const [creatingExp, setCreatingExp] = useState(false)
  const toggleSectionExpand = (id, type) => {
    const toggleProject = type === "project"
    const toggleTask = type === "task"
    const expansionState = toggleProject
      ? projectSectionExpanded
      : toggleTask
        ? taskSectionExpanded
        : expSectionExpanded
    const setExpansionState = toggleProject
      ? setProjectSectionExpanded
      : toggleTask
        ? setTaskSectionExpanded
        : setExpSectionExpanded
    const resetExpansion = (prev) => Object.fromEntries(Object.keys(prev).map((key) => [key, false]))
    setExpansionState((prev) => ({
      ...resetExpansion(prev),
      [id]: !expansionState[id],
    }))
    if (toggleProject) {
      setTaskSectionExpanded((prev) => resetExpansion(prev))
    }
    if (toggleProject || toggleTask) {
      setExpSectionExpanded((prev) => resetExpansion(prev))
    }
    creatingTask && setCreatingTask(false)
    creatingExp && setCreatingExp(false)
  }

  const doGetManager = (entity, key = "experiment_manager") => {
    const manager = entity ? entity?.[key] : config.user
    const activeMatch = activeUsers.find((u) => u.id === manager.id)
    return {
      type: "userDropdown",
      defaultOpt: manager,
      options: getDropdownUsers(activeMatch ? activeUsers : [...activeUsers, manager]),
    }
  }

  const expManagerField = {
    id: "fermmanagement.experiment.manager",
    msg: "Experiment Manager",
    value: (exp, _) => <LocaleFormattedUser user={exp.experiment_manager} />,
    key: "experiment_manager_id",
    extra: (exp) => doGetManager(exp),
  }

  const projectManagerField = {
    col: 2,
    id: "fermmanagement.project.manager",
    msg: "Project Manager",
    value: (proj, _) => <LocaleFormattedUser user={proj.project_manager} />,
    key: "project_manager_id",
    extra: (proj) => doGetManager(proj, "project_manager"),
  }

  const nameField = {
    col: 3,
    id: "general.name",
    msg: "Name",
    value: (entity, _) => <span>{entity.name || "-"}</span>,
  }
  const statusField = (cols = 1) => ({
    col: cols,
    id: "general.status",
    msg: "Status",
    value: (entity, _) => <ActiveSpan active={entity.active} getTextStr={getTextStr} />,
  })

  const projectFields = [
    {
      col: 2,
      id: "fermmanagement.netsuite.project.id",
      msg: "Netsuite Project ID",
      value: (proj, _) => <span>{proj.netsuite_project_id || "-"}</span>,
      key: "netsuite_project_id",
      extra: (_) => ({ type: "input" }),
    },
    nameField,
    {
      col: 1,
      id: "general.project.tasks",
      msg: "Tasks",
      value: (proj, _) => {
        const { tasks = [] } = proj
        return <EntityCountSpan entities={tasks} />
      },
    },
    {
      col: 2,
      id: "general.project.experiments",
      msg: "Experiments",
      value: (proj, _) => {
        const { tasks = [] } = proj
        return <EntityCountSpan entities={tasks.map((t) => t.experiments).flat()} />
      },
    },
    {
      col: 1,
      id: "general.runs",
      msg: "Runs",
      value: (proj, _) => {
        const { tasks = [] } = proj
        return (
          <EntityCountSpan
            entities={[...new Map(getTasksRuns(tasks).map((r) => [r.id, r])).values()]}
            activeKey={"live"}
          />
        )
      },
    },
    projectManagerField,
    statusField(),
  ]

  const taskFields = (type) =>
    [
      nameField,
      ...(type === "task"
        ? [
            {
              col: pageWidth > 850 ? 2 : 3,
              id: "general.project.experiments",
              msg: "Experiments",
              value: (task, _) => <EntityCountSpan entities={task.experiments} />,
            },
          ]
        : [{ col: pageWidth > 850 ? 2 : 3, ...expManagerField }]),
      {
        col: pageWidth > 850 ? 1 : 2,
        id: "general.runs",
        msg: "Runs",
        value: (entity, _) => {
          const runs =
            type === "task"
              ? [...new Map(getTasksRuns([entity]).map((r) => [r.id, r])).values()]
              : entity.runs
          return <EntityCountSpan entities={runs} activeKey={"live"} />
        },
      },
      statusField(pageWidth > 850 ? 1 : 2),
      {
        col: 2,
        id: "general.created.on",
        msg: "Created On",
        value: (task, _) => <span>{stringifyDate(task.created_on)}</span>,
      },
      {
        col: pageWidth > 850 ? 3 : 0,
        id: "general.created.by",
        msg: "Created By",
        value: (task, _) => <LocaleFormattedUser user={task.created_by} />,
      },
    ].filter((field) => field.col !== 0)

  const statuses = [...statusOptions, { id: "general.all", msg: "All", value: null }]
  const subfieldHeaderProps = {
    loadProjects: () => {
      clearFilters()
      loadProjects()
    },
    toggleSectionExpand: toggleSectionExpand,
    permissions: permissions,
    getTextStr: getTextStr,
    statuses: statuses,
    taskFields: taskFields,
    disabled: !!(loading || error),
  }
  const entityInfoProps = {
    intl: intl,
    getTextStr: getTextStr,
    onUpdate: loadProjects,
  }
  const projectColor = "var(--lanzalake-grey-light-2)"
  const taskColor = "var(--lanzalake-blue-bright)"
  const experimentColor = "var(--lanzalake-green-lighter)"

  const NoMatches = ({ color }) => {
    return (
      <div style={rowStyle(color)} className={"solid-grey-border"}>
        {getTextStr("general.no.matches", "No matches")}
      </div>
    )
  }
  const ToggleableRow = ({ type, uniqueKey, entity, isExpanded }) => {
    const isProject = type === "project"
    return (
      <div
        className={"row solid-grey-border text-center"}
        style={rowStyle(isProject ? projectColor : type === "task" ? taskColor : experimentColor)}
        onClick={() => toggleSectionExpand(String(entity.id), type)}
      >
        {(isProject ? projectFields : taskFields(type)).map((r, i) => (
          <div
            className={`col-${r.col}`}
            key={`${uniqueKey}${i}`}
            style={{ fontSize: "90%", padding: "5px" }}
          >
            {i === 0 && (
              <span
                style={{ float: "left" }}
                className={isExpanded ? "fa fa-caret-down" : "fa fa-caret-right"}
              />
            )}
            {r.value(entity, isExpanded)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <Modal show={show} onHide={onHide} backdrop={"static"} dialogClassName={"mw-100 wide-modal"}>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className={"fa fa-pencil-square-o"} />
          &nbsp;
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? (
          <PageErrorPanel attemptReload={() => loadProjects(true)} />
        ) : loading ? (
          <PageLoadingPanel />
        ) : (
          <>
            {projects.length > 0 && (
              <>
                <div className={"row"}>
                  <div className={"col-md-4"}>
                    <label>{getTextStr("general.project.task", "Task")}:</label>
                    <input
                      className={"form-control one-rem-margin-bottom"}
                      type={"text"}
                      value={taskTextFilter}
                      onChange={(e) => setTaskTextFilter(e.target.value)}
                      placeholder={getTextStr("general.search", "Search")}
                    />
                  </div>
                  <div className={"col-md-4"}>
                    <label>{getTextStr("general.project.experiment", "Experiment")}:</label>
                    <input
                      className={"form-control one-rem-margin-bottom"}
                      type={"text"}
                      value={expTextFilter}
                      onChange={(e) => setExpTextFilter(e.target.value)}
                      placeholder={getTextStr("general.search", "Search")}
                    />
                  </div>
                  <div className={"col-md-4"}>
                    {netsuiteProjects.length > 0 && (
                      <ReactSelectionElement
                        list={netsuiteProjects}
                        onValueChange={(_, selected) => setNetsuiteProjectFilter(selected)}
                        isClearable={true}
                        isMulti={true}
                        required={false}
                        label={getTextStr("fermmanagement.netsuite.project.id", "Netsuite Project ID")}
                      />
                    )}
                  </div>
                </div>
                <div className={"row"}>
                  <div className={"col-md-3"}>
                    <StatusDropdown
                      showLabel={true}
                      options={statuses}
                      defaultValue={statuses.find((s) => s.value === projectIsActive)}
                      setValue={(val) => setProjectIsActive(val)}
                      prefix={`[${getTextStr("general.project", "Project")}]`}
                    />
                  </div>
                  <div className={"col-md-3"}>
                    {projectManagers.length > 0 && (
                      <UserDropdown
                        field={"manager"}
                        current={projectManagerFilter}
                        setValue={(_, obj) => setProjectManagerFilter(obj)}
                        options={projectManagers}
                        label={getTextStr("fermmanagement.project.manager", "Project Manager")}
                        returnObj={true}
                        isClearable={true}
                        required={false}
                      />
                    )}
                  </div>
                  <div className={"col-md-3"}>
                    {experimentManagers.length > 0 && (
                      <UserDropdown
                        field={"exp_manager"}
                        current={experimentManagerFilter}
                        setValue={(_, obj) => setExperimentManagerFilter(obj)}
                        options={experimentManagers}
                        label={getTextStr("fermmanagement.experiment.manager", "Experiment Manager")}
                        returnObj={true}
                        isClearable={true}
                        required={false}
                      />
                    )}
                  </div>
                  <div className={"col-md-3"}>
                    {runOptions.length > 0 && (
                      <ReactSelectionElement
                        list={runOptions}
                        onValueChange={(_, selected) => setRunFilter(selected)}
                        isClearable={true}
                        isMulti={true}
                        required={false}
                        label={getTextStr("general.runs", "Runs")}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
            <div className={"row"} style={{ margin: "0" }}>
              {projectFields.map((r, i) => (
                <div
                  className={`col-${r.col} text-center solid-grey-border`}
                  key={`projHeader${i}`}
                  style={{ borderBottom: "none", padding: "5px" }}
                >
                  {r.id && getTextStr(r.id, r.msg)}
                </div>
              ))}
            </div>
            {finalProjects.length === 0 && <NoMatches color={projectColor} />}
            {finalProjects.map((project, projIDK) => {
              const { tasks: taskArr = [] } = project
              const taskSort = "name"
              const projectId = String(project.id)
              const isExpanded = projectSectionExpanded[projectId]
              let tasks = doActiveFilterArray(taskArr, "task")
                .filter((t) =>
                  taskTextFilter
                    ? `${t.name} ${t.description}`.toLowerCase().includes(taskTextFilter.toLowerCase())
                    : true,
                )
                .filter((t) =>
                  expTextFilter
                    ? t.experiments
                        .map((e) => `${e.name} ${e.description}`)
                        .join(", ")
                        .toLowerCase()
                        .includes(expTextFilter.toLowerCase())
                    : true,
                )
                .filter((t) =>
                  experimentManagerFilter?.id
                    ? t.experiments.map((e) => e.experiment_manager_id).includes(experimentManagerFilter.id)
                    : true,
                )
              if (runNames.length > 0) {
                tasks = tasks.filter((t) => {
                  const taskRuns = new Set(getTasksRuns([t]).map((r) => r.name))
                  return runNames.some((n) => taskRuns.has(n))
                })
              }
              tasks = tasks.sort((a, b) => a[taskSort].localeCompare(b[taskSort]))
              if (isExpanded && tasks.length === 1 && !taskSectionExpanded[tasks[0]?.id]) {
                toggleSectionExpand(tasks[0]?.id, "task")
              }
              const taskCreating = isExpanded && (creatingTask || taskArr.length === 0)
              const entitySpecific = projectFields
                .filter((r) => r.key)
                .map((r) => {
                  const { value, col, extra, ...entity } = r
                  return {
                    ...entity,
                    value: r.value(project),
                    ...r.extra(project),
                  }
                })
              return (
                <div key={`project${projIDK}`}>
                  <ToggleableRow
                    type={"project"}
                    uniqueKey={`project${projIDK}`}
                    entity={project}
                    isExpanded={isExpanded}
                  />
                  {isExpanded && (
                    <div className={"solid-grey-border"} style={{ borderRadius: "5px" }}>
                      <EntityInformation
                        title={getTextStr("general.project", "Project")}
                        entity={project}
                        extraFields={entitySpecific}
                        updateUrl={`/warehouse/projects/${project.id}/update_project/`}
                        canEdit={permissions.includes("edit_project")}
                        sectionColor={projectColor}
                        canDelete={new Set(getTasksRuns(tasks).map((r) => r.id)).size === 0}
                        deleteBranchUrl={`/warehouse/projects/${project.id}/delete_project/`}
                        deleteAlert={{
                          [getTextStr("general.project", "Project")]: [project],
                          [getTextStr("general.project.tasks", "Tasks")]: project.tasks,
                          [getTextStr("general.project.experiments", "Experiments")]: project.tasks
                            .map((t) => t.experiments)
                            .flat(),
                        }}
                        {...entityInfoProps}
                      />
                      <div style={{ padding: "20px" }}>
                        <SubfieldHeader
                          onToggle={() => setCreatingTask(!creatingTask)}
                          entity={project}
                          creating={taskCreating}
                          parentID={projectId}
                          type={"task"}
                          nonFilterArr={taskArr}
                          isActive={taskIsActive}
                          setIsActive={setTaskIsActive}
                          {...subfieldHeaderProps}
                        />
                        {tasks.length > 0
                          ? tasks.map((task) => {
                              const { experiments: expArr = [] } = task
                              const taskID = String(task.id)
                              const taskIsExpanded = taskSectionExpanded[taskID]
                              const expSort = "name"
                              let experiments = doActiveFilterArray(expArr, "experiment")
                                .filter((e) =>
                                  expTextFilter
                                    ? `${e.name} ${e.description}`
                                        .toLowerCase()
                                        .includes(expTextFilter.toLowerCase())
                                    : true,
                                )
                                .filter((e) =>
                                  experimentManagerFilter?.id
                                    ? e.experiment_manager_id === experimentManagerFilter.id
                                    : true,
                                )
                              if (runNames.length > 0) {
                                experiments = experiments.filter((e) => {
                                  const expRuns = new Set((e.runs || []).map((r) => r.name).flat())
                                  return runNames.some((n) => expRuns.has(n))
                                })
                              }
                              experiments = experiments.sort((a, b) => a[expSort].localeCompare(b[expSort]))
                              if (
                                taskIsExpanded &&
                                experiments.length === 1 &&
                                !expSectionExpanded[experiments[0]?.id]
                              ) {
                                toggleSectionExpand(experiments[0]?.id, "experiment")
                              }
                              const expCreating = taskIsExpanded && (creatingExp || expArr.length === 0)
                              return (
                                <div key={`p${projectId}t${taskID}`}>
                                  <ToggleableRow
                                    type={"task"}
                                    uniqueKey={`p${projectId}t${taskID}`}
                                    entity={task}
                                    isExpanded={taskIsExpanded}
                                  />
                                  {taskIsExpanded && (
                                    <div className={"solid-grey-border"} style={{ borderRadius: "5px" }}>
                                      <EntityInformation
                                        title={getTextStr("general.project.task", "Task")}
                                        entity={task}
                                        updateUrl={`/warehouse/project_tasks/${task.id}/update_task/`}
                                        canEdit={permissions.includes("edit_project_task")}
                                        sectionColor={taskColor}
                                        extraFields={[
                                          { id: "general.project", msg: "Project", value: project.name },
                                          {
                                            id: "fermmanagement.netsuite.project.id",
                                            msg: "Netsuite Project ID",
                                            value: project.netsuite_project_id,
                                          },
                                        ]}
                                        canDelete={new Set(getTasksRuns([task]).map((r) => r.id)).size === 0}
                                        deleteBranchUrl={`/warehouse/project_tasks/${task.id}/delete_task/`}
                                        deleteAlert={{
                                          [getTextStr("general.project.task", "Task")]: [task],
                                          [getTextStr("general.project.experiments", "Experiments")]:
                                            task.experiments,
                                        }}
                                        {...entityInfoProps}
                                      />
                                      <div style={{ padding: "20px" }}>
                                        <SubfieldHeader
                                          onToggle={() => setCreatingExp(!creatingExp)}
                                          entity={task}
                                          creating={expCreating}
                                          type={"experiment"}
                                          parentID={taskID}
                                          nonFilterArr={expArr}
                                          isActive={expIsActive}
                                          setIsActive={setExpIsActive}
                                          expManagerField={{ ...expManagerField, ...expManagerField.extra() }}
                                          {...subfieldHeaderProps}
                                        />
                                        <>
                                          {experiments.length > 0
                                            ? experiments.map((experiment) => {
                                                const { runs = [] } = experiment
                                                const expID = String(experiment.id)
                                                const expIsExpanded = expSectionExpanded[expID]
                                                const entitySpecific = taskFields("experiment")
                                                  .filter((r) => r.key)
                                                  .map((r) => {
                                                    const { value, col, extra, ...entity } = r
                                                    return {
                                                      ...entity,
                                                      value: r.value(experiment),
                                                      ...r.extra(experiment),
                                                    }
                                                  })
                                                return (
                                                  <div key={`p${projectId}t${taskID}e${expID}}`}>
                                                    <ToggleableRow
                                                      type={"experiment"}
                                                      uniqueKey={`p${projectId}t${taskID}e${expID}}`}
                                                      entity={experiment}
                                                      isExpanded={expIsExpanded}
                                                    />
                                                    {expIsExpanded && (
                                                      <div
                                                        className={"solid-grey-border"}
                                                        style={{ borderRadius: "5px" }}
                                                      >
                                                        <EntityInformation
                                                          title={getTextStr(
                                                            "general.project.experiment",
                                                            "Experiment",
                                                          )}
                                                          entity={experiment}
                                                          updateUrl={`/warehouse/experiments/${experiment.id}/update_experiment/`}
                                                          canEdit={permissions.includes("edit_experiment")}
                                                          sectionColor={experimentColor}
                                                          extraFields={[
                                                            ...entitySpecific,
                                                            {
                                                              id: "general.project",
                                                              msg: "Project",
                                                              value: project.name,
                                                            },
                                                            {
                                                              id: "general.project.task",
                                                              msg: "Task",
                                                              value: task.name,
                                                            },
                                                            {
                                                              id: "fermmanagement.netsuite.project.id",
                                                              msg: "Netsuite Project ID",
                                                              value: project.netsuite_project_id,
                                                            },
                                                          ]}
                                                          canDelete={
                                                            new Set(runs.map((r) => r.id)).size === 0
                                                          }
                                                          deleteBranchUrl={`/warehouse/experiments/${experiment.id}/delete_experiment/`}
                                                          deleteAlert={{
                                                            [getTextStr(
                                                              "general.project.experiment",
                                                              "Experiment",
                                                            )]: [experiment],
                                                          }}
                                                          {...entityInfoProps}
                                                        />
                                                        <RunTable
                                                          runs={runs}
                                                          intl={intl}
                                                          pageSize={5}
                                                          showExperiments={IS_PROJECT_EXPERIMENT_SITE}
                                                          canClickRow={false}
                                                          canTableFilter={true}
                                                        />
                                                      </div>
                                                    )}
                                                  </div>
                                                )
                                              })
                                            : expArr.length > 0 && <NoMatches color={experimentColor} />}
                                        </>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })
                          : taskArr.length > 0 && <NoMatches color={taskColor} />}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
            <NewProjectForm
              getTextStr={getTextStr}
              disabled={loading || error}
              onSuccess={(projID) => {
                clearFilters()
                loadProjects()
                toggleSectionExpand(projID, "project")
              }}
              projectManagerField={{ ...projectManagerField, ...projectManagerField.extra() }}
              permission={permissions.includes("edit_project")}
            />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant={"outline-primary"} onClick={onHide}>
          {getTextStr("general.close", "Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withAppSettingsConfig(withFermManagementConfig(ProjectHierarchyModal))
